import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { CustomerService } from '../../../services/customer.service';
import { LeadStatus, LeadSource } from '../../../classes/crm';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-lead-status-card',
  templateUrl: './lead-status-card.component.html',
  styleUrls: ['./lead-status-card.component.scss']
})
export class LeadStatusCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() customerdata: any;
  @Output() customerUpdated = new EventEmitter<any>();

  leadStatuses: LeadStatus[] = [];
  leadSources: LeadSource[] = [];
  editingLeadStatus: boolean = false;
  selectedLeadStatusId: number;
  selectedLeadSourceId: number;
  leadScore: number;
  nextFollowUpDate: string;

  private destroy$ = new Subject<void>();

  constructor(
    private globalSearchService: GlobalSearchService,
    private customerService: CustomerService
  ) { }

  ngOnInit(): void {
    this.globalSearchService.lead_statuses
      .pipe(takeUntil(this.destroy$))
      .subscribe(statuses => {
        this.leadStatuses = statuses;
        this.initializeForm();
      });

    this.globalSearchService.crm_types
      .pipe(takeUntil(this.destroy$))
      .subscribe(crmTypes => {
        if (crmTypes && crmTypes.leadsources) {
          this.leadSources = crmTypes.leadsources;
          this.initializeForm();
        }
      });
  }

  initializeForm(): void {
    if (this.customerdata?.customer) {
      this.selectedLeadStatusId = this.customerdata.customer.lead_status_id || null;
      this.selectedLeadSourceId = this.customerdata.customer.lead_source_id || null;
      this.leadScore = this.customerdata.customer.lead_score || 0;
      this.nextFollowUpDate = this.customerdata.customer.next_follow_up_date || '';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerdata'] && !changes['customerdata'].firstChange) {
      this.initializeForm();
    }
  }

  toggleEdit(): void {
    this.editingLeadStatus = !this.editingLeadStatus;
    if (!this.editingLeadStatus) {
      this.initializeForm(); // Reset if cancelling edit
    }
  }

  saveLeadStatus(): void {
    if (!this.customerdata?.debtorno) {
      console.error('Customer data not available');
      return;
    }

    this.customerService.updateLeadStatus(
      this.customerdata.debtorno,
      this.customerdata.branchcode || 'MAIN',
      this.selectedLeadStatusId,
      this.selectedLeadSourceId,
      this.leadScore
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (result: any) => {
        // Reload customer data to get updated structure
        this.customerUpdated.emit(true);
        this.editingLeadStatus = false;
      },
      error => {
        console.error('Error updating lead status:', error);
        alert('Failed to update lead status. Please try again.');
      }
    );
  }

  updateFollowUp(): void {
    if (!this.customerdata?.debtorno) {
      console.error('Customer data not available');
      return;
    }

    this.customerService.updateFollowUpDate(
      this.customerdata.debtorno,
      this.customerdata.branchcode || 'MAIN',
      this.nextFollowUpDate
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (result: any) => {
        // Reload customer data to get updated structure
        this.customerUpdated.emit(true);
      },
      error => {
        console.error('Error updating follow-up date:', error);
        alert('Failed to update follow-up date. Please try again.');
      }
    );
  }

  getLeadStatusColor(statusId: number): string {
    const statuses = this.globalSearchService.lead_statuses.getValue();
    if (statuses) {
      return statuses.find(s => s.id === statusId)?.color || '#6c757d';
    }
    return '#6c757d';
  }

  getScoreClass(score: number): string {
    if (!score) return 'score-low';
    if (score >= 70) return 'score-high';
    if (score >= 40) return 'score-medium';
    return 'score-low';
  }

  getDaysSinceContact(): number {
    if (!this.customerdata?.customer?.last_contact_date) return 0;
    const lastContact = new Date(this.customerdata.customer.last_contact_date);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - lastContact.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getConversionRate(): number {
    // Mock conversion rate calculation - in real app this would come from backend
    const score = this.customerdata?.customer?.lead_score || 0;
    return Math.min(Math.round(score * 0.8), 100);
  }

  scheduleFollowUp(): void {
    // Quick action to schedule follow-up
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.nextFollowUpDate = tomorrow.toISOString().split('T')[0];
    this.updateFollowUp();
  }

  getLeadStatusName(statusId: number): string {
    if (!statusId) return '';
    const status = this.leadStatuses.find(s => s.id === statusId);
    return status?.name || '';
  }

  getLeadSourceName(sourceId: number): string {
    if (!sourceId) return '';
    const source = this.leadSources.find(s => s.id === sourceId);
    return source?.name || '';
  }

  logActivity(): void {
    // Quick action to log activity - scroll to activities timeline section
    console.log('Log activity clicked for customer:', this.customerdata?.debtorno);
    // Scroll to activities timeline section
    const activitiesElement = document.querySelector('app-customer-activities-timeline');
    if (activitiesElement) {
      activitiesElement.scrollIntoView({ behavior: 'smooth' });
      // Trigger the create form
      setTimeout(() => {
        const createBtn = activitiesElement.querySelector('.btn-primary');
        if (createBtn) {
          (createBtn as HTMLElement).click();
        }
      }, 500);
    }
  }

  createOpportunity(): void {
    // Quick action to create opportunity - scroll to opportunities section
    console.log('Create opportunity clicked for customer:', this.customerdata?.debtorno);
    // Scroll to opportunities section
    const opportunitiesElement = document.querySelector('app-customer-opportunities');
    if (opportunitiesElement) {
      opportunitiesElement.scrollIntoView({ behavior: 'smooth' });
      // Trigger the create form
      setTimeout(() => {
        const createBtn = opportunitiesElement.querySelector('.btn-primary');
        if (createBtn) {
          (createBtn as HTMLElement).click();
        }
      }, 500);
    }
  }

  viewHistory(): void {
    // Quick action to view history - scroll to activities timeline
    console.log('View history clicked for customer:', this.customerdata?.debtorno);
    // Scroll to activities timeline section
    const activitiesElement = document.querySelector('app-customer-activities-timeline');
    if (activitiesElement) {
      activitiesElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
